import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { Layout } from '../components/layout/Layout';
import { performanceApi } from '../lib/api';
import { TradeExecution, Position, ClosedPnL } from '../lib/types';
import { Skeleton } from '../components/ui/Skeleton';
import { DateRangePickerNew as DateRangePicker } from '../components/ui/DateRangePickerNew';
import { TrendingUp, Users, DollarSign, Activity, Clock, Download } from 'lucide-react';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { exportTradingDataToExcel } from '../utils/excelExport';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercentage = (value: number) => {
  return `${value.toFixed(2)}%`;
};

const formatDateDDMMYYYY = (timestamp: string) => {
  const date = new Date(parseInt(timestamp));
  return format(date, 'dd/MM/yyyy HH:mm');
};

const calculateTimeInMarket = (entryTime: string, exitTime: string) => {
  const entry = parseInt(entryTime);
  const exit = parseInt(exitTime);
  const diffMs = exit - entry;
  const diffHours = diffMs / (1000 * 60 * 60);
  
  if (diffHours < 24) {
    return `${diffHours.toFixed(1)}h`;
  }
  const days = Math.floor(diffHours / 24);
  const hours = diffHours % 24;
  return `${days}d ${hours.toFixed(1)}h`;
};

const MetricCard: React.FC<{
  title: string;
  value: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  description?: string;
}> = ({ title, value, icon, trend, description }) => {
  const getTrendColor = () => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className={`text-2xl font-bold ${getTrendColor()}`}>{value}</p>
          {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
        </div>
        <div className={`${getTrendColor()}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const ClosedPnLRow: React.FC<{ closedPnL: ClosedPnL; userName: string }> = ({ closedPnL, userName }) => {
  const pnl = parseFloat(closedPnL.closedPnl || '0');
  const isProfit = pnl > 0;
  const entryPrice = parseFloat(closedPnL.avgEntryPrice || '0');
  const exitPrice = parseFloat(closedPnL.avgExitPrice || '0');
  const qty = parseFloat(closedPnL.qty || '0');
  const timeInMarket = calculateTimeInMarket(closedPnL.createdTime, closedPnL.updatedTime);
  
  // Additional calculated fields
  const openTradeVolume = parseFloat(closedPnL.cumEntryValue || '0');
  const closedTradeVolume = parseFloat(closedPnL.cumExitValue || '0');
  const openingFee = parseFloat(closedPnL.openingFee || '0');
  const closingFee = parseFloat(closedPnL.closingFee || '0');
  

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900">{userName}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{closedPnL.symbol}</td>
      <td className="px-4 py-3 text-sm">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          closedPnL.side === 'Buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {closedPnL.side}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{qty.toFixed(4)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(entryPrice)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(exitPrice)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{closedPnL.orderType || closedPnL.execType || '-'}</td>
      <td className="px-4 py-3 text-sm">
        <span className={`font-semibold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(pnl)}
        </span>
      </td>
      <td className="px-4 py-3 text-sm">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          isProfit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {isProfit ? 'Win' : 'Loss'}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(openTradeVolume)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(closedTradeVolume)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{openingFee > 0 ? formatCurrency(openingFee) : '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{closingFee > 0 ? formatCurrency(closingFee) : '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{closedPnL.leverage}x</td>
      <td className="px-4 py-3 text-sm text-gray-600">{timeInMarket}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(closedPnL.createdTime)}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(closedPnL.updatedTime)}</td>
    </tr>
  );
};

const ExecutionRow: React.FC<{ trade: TradeExecution; userName: string }> = ({ trade, userName }) => {
  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900">{userName}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{trade.symbol}</td>
      <td className="px-4 py-3 text-sm">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          trade.side === 'Buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {trade.side}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{parseFloat(trade.execQty).toFixed(4)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(parseFloat(trade.execPrice))}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(parseFloat(trade.execValue))}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(parseFloat(trade.execFee))}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{trade.isMaker ? 'Maker' : 'Taker'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(trade.execTime)}</td>
    </tr>
  );
};

const PositionRow: React.FC<{ position: Position; userName: string }> = ({ position, userName }) => {
  const size = parseFloat(position.size);
  const unrealisedPnl = parseFloat(position.unrealisedPnl || '0');
  const isProfit = unrealisedPnl > 0;
  const isActive = size > 0;

  if (!isActive) return null;

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900">{userName}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{position.symbol}</td>
      <td className="px-4 py-3 text-sm">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          position.side === 'Buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {position.side}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{size.toFixed(4)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(parseFloat(position.avgPrice))}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(parseFloat(position.markPrice))}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{position.leverage}x</td>
      <td className="px-4 py-3 text-sm">
        <span className={`font-semibold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(unrealisedPnl)}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(position.createdTime)}</td>
    </tr>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-24" />
      ))}
    </div>
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    </div>
  </div>
);

export const Performance: React.FC = () => {
  // Default to last 30 days
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });

  const { data: performanceData, isLoading, error, refetch } = useQuery({
    queryKey: ['performance', dateRange],
    queryFn: async () => {
      const response = await performanceApi.getPerformanceData({
        startDate: dateRange?.from?.toISOString(),
        endDate: dateRange?.to?.toISOString(),
      });
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: !!dateRange?.from && !!dateRange?.to,
  });

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
  };

  const handleExportToExcel = () => {
    if (!performanceData) return;
    
    exportTradingDataToExcel({
      users: performanceData.users,
      aggregate: performanceData.aggregate,
      dateRange: dateRange
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
          </div>
          <LoadingSkeleton />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
          <div className="p-4 bg-red-50 text-red-700 rounded-md">
            Error loading performance data: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </div>
      </Layout>
    );
  }

  if (!performanceData) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
          <div className="p-4 bg-gray-50 text-gray-700 rounded-md">
            No performance data available
          </div>
        </div>
      </Layout>
    );
  }

  const { users, aggregate } = performanceData;
  
  // Create stable arrays without useMemo to avoid hooks order issues
  const allClosedPnL = users.flatMap(user => 
    user.closedPnL.map(pnl => ({ ...pnl, userName: user.userName }))
  );
  const allTrades = users.flatMap(user => 
    user.trades.map(trade => ({ ...trade, userName: user.userName }))
  );
  const allPositions = users.flatMap(user => 
    user.positions.filter(pos => parseFloat(pos.size) > 0).map(pos => ({ ...pos, userName: user.userName }))
  );

  // Prepare chart data - cumulative P&L over time with trade details
  const chartData: Array<{
    date: string;
    fullDate: string;
    cumulativePnL: number;
    tradePnL: number;
    tradePnLBar: number; // For bar chart (will be positive/negative)
    tradeCount: number;
    symbol: string;
    userName: string;
    entryPrice: number;
    exitPrice: number;
    side: string;
    quantity: number;
  }> = [];
  
  if (allClosedPnL && allClosedPnL.length > 0) {
    // Sort all closed trades by completion time
    const sortedTrades = [...allClosedPnL].sort((a, b) => 
      parseInt(a.updatedTime) - parseInt(b.updatedTime)
    );

    let cumulativePnL = 0;
    chartData.push(...sortedTrades.map((trade, index) => {
      const pnl = parseFloat(trade.closedPnl || '0');
      const entryPrice = parseFloat(trade.avgEntryPrice || '0');
      const exitPrice = parseFloat(trade.avgExitPrice || '0');
      const quantity = parseFloat(trade.qty || '0');
      cumulativePnL += pnl;
      
      return {
        date: format(new Date(parseInt(trade.updatedTime)), 'MMM dd'),
        fullDate: format(new Date(parseInt(trade.updatedTime)), 'dd/MM/yyyy HH:mm'),
        cumulativePnL: cumulativePnL,
        tradePnL: pnl,
        tradePnLBar: pnl, // Same value but used for bars
        tradeCount: index + 1,
        symbol: trade.symbol,
        userName: trade.userName,
        entryPrice: entryPrice,
        exitPrice: exitPrice,
        side: trade.side,
        quantity: quantity
      };
    }));
  }

  const aggregatePnlTrend = aggregate.totalPnl > 0 ? 'up' : aggregate.totalPnl < 0 ? 'down' : 'neutral';

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
            <p className="text-sm text-gray-600 mt-1">
              Production trading data • Updated: {performanceData ? format(new Date(performanceData.timestamp), 'dd/MM/yyyy HH:mm') : 'Loading...'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              className="w-full sm:w-auto"
            />
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Refresh
            </button>
            <button
              onClick={handleExportToExcel}
              disabled={!performanceData}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export to Excel
            </button>
          </div>
        </div>

        {/* Aggregate Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total P&L (USDT)"
            value={formatCurrency(aggregate.totalPnl)}
            icon={<DollarSign className="h-8 w-8" />}
            trend={aggregatePnlTrend}
            description="All users combined"
          />
          <MetricCard
            title="Active Traders"
            value={aggregate.totalUsers.toString()}
            icon={<Users className="h-8 w-8" />}
            description="Users with production API keys"
          />
          <MetricCard
            title="Total Trades"
            value={aggregate.totalTrades.toString()}
            icon={<Activity className="h-8 w-8" />}
            description={`Executed in selected period`}
          />
          <MetricCard
            title="Avg Win Rate"
            value={formatPercentage(aggregate.averageWinRate)}
            icon={<TrendingUp className="h-8 w-8" />}
            trend={aggregate.averageWinRate > 50 ? 'up' : 'down'}
            description="Across all traders"
          />
        </div>

        {/* User Performance Summary */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Trader Performance Summary</h2>
            <p className="text-sm text-gray-600 mt-1">Individual trader performance metrics</p>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {users.map((user) => (
                <div key={user.userId} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{user.userName}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard
                      title="Total P&L (USDT)"
                      value={formatCurrency(user.totalPnl)}
                      icon={<DollarSign className="h-5 w-5" />}
                      trend={user.totalPnl > 0 ? 'up' : user.totalPnl < 0 ? 'down' : 'neutral'}
                      description={`${user.totalTrades} trades`}
                    />
                    <MetricCard
                      title="Win Rate"
                      value={formatPercentage(user.winRate)}
                      icon={<TrendingUp className="h-5 w-5" />}
                      trend={user.winRate > 50 ? 'up' : 'down'}
                      description="Success rate"
                    />
                    <MetricCard
                      title="Total Volume"
                      value={formatCurrency(user.totalVolume)}
                      icon={<Activity className="h-5 w-5" />}
                      description="Trading volume"
                    />
                    <MetricCard
                      title="Best Trade"
                      value={formatCurrency(user.largestWin)}
                      icon={<TrendingUp className="h-5 w-5" />}
                      trend="up"
                      description={`Worst: ${formatCurrency(user.largestLoss)}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trading Fees Summary */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Trading Fees Summary</h2>
            <p className="text-sm text-gray-600 mt-1">Aggregate trading fees across all traders</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Opening Fees"
                value={formatCurrency(
                  allClosedPnL.reduce((sum, trade) => sum + parseFloat(trade.openingFee || '0'), 0)
                )}
                icon={<DollarSign className="h-6 w-6" />}
                description="Sum of all opening fees"
              />
              <MetricCard
                title="Total Closing Fees"
                value={formatCurrency(
                  allClosedPnL.reduce((sum, trade) => sum + parseFloat(trade.closingFee || '0'), 0)
                )}
                icon={<DollarSign className="h-6 w-6" />}
                description="Sum of all closing fees"
              />
              <MetricCard
                title="Funding Fees"
                value="Not Tracked"
                icon={<Clock className="h-6 w-6" />}
                description="Funding rates applied every 8h (separate from trade P&L)"
              />
              <MetricCard
                title="Total Fees"
                value={formatCurrency(
                  allClosedPnL.reduce((sum, trade) => 
                    sum + parseFloat(trade.openingFee || '0') + parseFloat(trade.closingFee || '0'), 0
                  )
                )}
                icon={<DollarSign className="h-6 w-6" />}
                trend="neutral"
                description="Opening + Closing fees"
              />
            </div>
          </div>
        </div>

        {/* Performance Chart */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Trading Performance Analysis</h2>
            <p className="text-sm text-gray-600 mt-1">Cumulative P&L (blue line) and individual trade results (green/red bars)</p>
          </div>
          <div className="p-6">
            {chartData.length > 0 ? (
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6b7280"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      yAxisId="left"
                      stroke="#6b7280"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${value.toFixed(0)}`}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      stroke="#6b7280"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${value.toFixed(0)}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
                              <p className="font-medium text-gray-900">{data.fullDate}</p>
                              <p className="text-sm text-gray-600">Trade #{data.tradeCount}</p>
                              <div className="mt-2 space-y-1">
                                <p className="text-sm">
                                  <span className="text-gray-600">Trader:</span> {data.userName}
                                </p>
                                <p className="text-sm">
                                  <span className="text-gray-600">Symbol:</span> {data.symbol}
                                </p>
                                <p className="text-sm">
                                  <span className="text-gray-600">Side:</span> {data.side}
                                </p>
                                <p className="text-sm">
                                  <span className="text-gray-600">Quantity:</span> {data.quantity.toFixed(4)}
                                </p>
                                <p className="text-sm">
                                  <span className="text-gray-600">Entry Price:</span> ${data.entryPrice.toFixed(2)}
                                </p>
                                <p className="text-sm">
                                  <span className="text-gray-600">Exit Price:</span> ${data.exitPrice.toFixed(2)}
                                </p>
                                <p className="text-sm">
                                  <span className="text-gray-600">Trade P&L:</span>{' '}
                                  <span className={data.tradePnL >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    ${data.tradePnL.toFixed(2)}
                                  </span>
                                </p>
                                <p className="text-sm font-medium">
                                  <span className="text-gray-600">Cumulative P&L:</span>{' '}
                                  <span className={data.cumulativePnL >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    ${data.cumulativePnL.toFixed(2)}
                                  </span>
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="2 2" />
                    <Bar
                      yAxisId="right"
                      dataKey="tradePnLBar"
                      opacity={0.7}
                      name="Trade P&L"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.tradePnLBar >= 0 ? '#10b981' : '#ef4444'} />
                      ))}
                    </Bar>
                    <Line
                      yAxisId="left"
                      type="linear"
                      dataKey="cumulativePnL"
                      stroke="#3b82f6"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{
                        r: 4,
                        stroke: '#3b82f6',
                        strokeWidth: 2,
                        fill: '#ffffff'
                      }}
                      name="Cumulative P&L"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Trading Data</h3>
                  <p className="text-gray-600">No closed trades available for the selected period.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Current Positions */}
        {allPositions.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Current Open Positions</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trader</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Side</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mark Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leverage</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unrealized P&L (USDT)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opened (UTC)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allPositions.map((position, index) => (
                    <PositionRow key={`${position.symbol}-${index}`} position={position} userName={position.userName} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Closed Trades with P&L */}
        {allClosedPnL.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Closed Trades with P&L</h2>
              <p className="text-sm text-gray-600 mt-1">Completed trades with final profit/loss</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trader</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Side</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trade Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closed P&L (USDT)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Open Trade Volume</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closed Trade Volume</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opening Fee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closing Fee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leverage</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time in Market</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Time (UTC)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit Time (UTC)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allClosedPnL
                    .sort((a, b) => parseInt(b.updatedTime) - parseInt(a.updatedTime))
                    .slice(0, 50) // Show last 50 closed trades
                    .map((closedPnL, index) => (
                      <ClosedPnLRow key={`${closedPnL.orderId}-${index}`} closedPnL={closedPnL} userName={closedPnL.userName} />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* All Trade Executions */}
        {allTrades.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">All Trade Executions</h2>
              <p className="text-sm text-gray-600 mt-1">Individual executions including open trades, partial fills, and closed trades</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trader</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Side</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time (UTC)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allTrades
                    .sort((a, b) => parseInt(b.execTime) - parseInt(a.execTime))
                    .slice(0, 100) // Show last 100 executions
                    .map((trade, index) => (
                      <ExecutionRow key={`${trade.execId}-${index}`} trade={trade} userName={trade.userName} />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {allClosedPnL.length === 0 && allTrades.length === 0 && allPositions.length === 0 && (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="text-gray-400 mb-4">
              <Activity className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Trading Activity</h3>
            <p className="text-gray-600">
              No trades or positions found in the selected period for users with production API keys.
            </p>
          </div>
        )}

        <div className="text-xs text-gray-500 text-center">
          {performanceData.note}
        </div>
      </div>
    </Layout>
  );
};